import cloneDeep from 'lodash/cloneDeep';
import { EntryShell, InvApplication } from '../interfaces';
import { hasBooleanAccess } from '../security/permission.service';
import { Application } from './application';

export const CODE_TABLE_ADMINISTRATION_SEC_ATTR_ID: number = 5;
export const BILLING_OPERATIONS_SEC_ATTR_ID: number = 9600;
export const CARE_SEC_ATTR_ID = 2000;
export const CONFIGURATION_SEC_ATTR_ID = 1000;
export const REPORTING_SEC_ATTR_ID = 4000;
export const SECURITY_SEC_ATTR_ID = 6000;
export const STUDIO_SEC_ATTR_ID = 7000;
export const WORKBENCH_SEC_ATTR_ID = 5000;
export const DOCUMENTATION_SEC_ATTR_ID = 9900;
export const RATINGANDCHARGING_SEC_ATTR_ID = 13002;

export const allApplications: Record<Application, InvApplication> = {
  [Application.Billing]: {
    glyph: 'billing',
    // t('Billing');
    name: 'Billing',
    cssName: 'billing',
    isActive: false,
    isDeployed: true,
    isStandalone: true,
    securityAttributeId: BILLING_OPERATIONS_SEC_ATTR_ID,
    basePath: '/billing2',
    angularBasePath: '/billing',
    preferenceKey: Application.Billing,
    sRef: 'index.billing',
    order: 5
  },
  [Application.Configuration]: {
    glyph: 'configuration',
    // t('Configuration');
    name: 'Configuration',
    cssName: 'configuration',
    isActive: false,
    isDeployed: true,
    isStandalone: true,
    securityAttributeId: CONFIGURATION_SEC_ATTR_ID,
    basePath: '/configuration',
    preferenceKey: Application.Configuration,
    sRef: 'index.configuration',
    authorizeFn: hasConfigAccess,
    order: 7
  },
  [Application.CustomerCare]: {
    glyph: 'customer_care',
    // t('Customer Care');
    name: 'Customer Care',
    cssName: 'customerCare',
    isActive: false,
    isDeployed: true,
    isStandalone: true,
    securityAttributeId: CARE_SEC_ATTR_ID,
    basePath: '/customercare2',
    angularBasePath: '/customercare',
    preferenceKey: Application.CustomerCare,
    sRef: 'index.customercare',
    order: 1
  },
  [Application.Documentation]: {
    glyph: 'documentation',
    // t('Documentation');
    name: 'Documentation',
    cssName: 'documentation',
    isActive: false,
    isDeployed: true,
    isStandalone: true,
    securityAttributeId: DOCUMENTATION_SEC_ATTR_ID,
    basePath: '/documentation',
    preferenceKey: Application.Documentation,
    sRef: 'index.documentation',
    order: 9
  },
  [Application.Reporting]: {
    glyph: 'reports',
    // t('Reporting');
    name: 'Reporting',
    cssName: 'reporting',
    isActive: false,
    isDeployed: true,
    isStandalone: true,
    securityAttributeId: REPORTING_SEC_ATTR_ID,
    basePath: '/reporting2',
    angularBasePath: '/reporting',
    defaultEntryShell: EntryShell.Angular,
    preferenceKey: Application.Reporting,
    enableHomeLink: true,
    sRef: 'index.reporting',
    order: 3
  },
  [Application.Security]: {
    glyph: 'security',
    // t('Security');
    name: 'Security',
    cssName: 'security',
    isActive: false,
    isDeployed: true,
    isStandalone: true,
    securityAttributeId: SECURITY_SEC_ATTR_ID,
    basePath: '/security',
    preferenceKey: Application.Security,
    sRef: 'index.security',
    order: 8
  },
  [Application.Studio]: {
    glyph: 'studio',
    // t('Studio');
    name: 'Studio',
    cssName: 'studio',
    isActive: false,
    isDeployed: true,
    isStandalone: true,
    securityAttributeId: STUDIO_SEC_ATTR_ID,
    basePath: '/studio2',
    angularBasePath: '/studio',
    defaultEntryShell: EntryShell.Angular,
    preferenceKey: Application.Studio,
    sRef: 'index.studio',
    order: 2
  },
  [Application.RatingAndCharging]: {
    glyph: 'configuration',
    // t('Rating and Charging');
    name: 'Rating and Charging',
    isActive: false,
    isDeployed: true,
    isStandalone: true,
    securityAttributeId: RATINGANDCHARGING_SEC_ATTR_ID,
    basePath: '/ratingandcharging',
    preferenceKey: Application.RatingAndCharging,
    order: 4
  },
  [Application.Workbench]: {
    glyph: 'configuration',
    // t('Workbench');
    name: 'Workbench',
    isActive: false,
    isDeployed: true,
    isStandalone: true,
    securityAttributeId: WORKBENCH_SEC_ATTR_ID,
    basePath: '/workbench',
    preferenceKey: Application.Workbench,
    order: 6
  }
};

export function determineApplicationAccessForSecurityAttributes(
  securityAttributes: any
) {
  const myApplications: InvApplication[] = [];

  Object.values(allApplications).forEach((application) => {
    let isAuthorized = false;

    if (application.isDeployed) {
      isAuthorized = application.authorizeFn
        ? application.authorizeFn(securityAttributes, application)
        : hasBooleanAccess(securityAttributes, application.securityAttributeId);
    }

    if (isAuthorized) {
      myApplications.push(application);
    }
  });
  return myApplications;
}

/**
 * Build the URL of a page that is still served by an application's AngularJS
 * shell. Only the applications that still have an Angular counterpart expose
 * an `angularBasePath`, and only those shells serve routes under `/app`.
 *
 * @param application - Application the page belongs to.
 * @param path - Page path within the Angular shell, including the leading "/".
 * @returns The Angular URL, or `undefined` when the application has no Angular
 * shell and therefore no such page.
 */
export function getAngularModuleUrl(
  application: InvApplication,
  path: string
): string | undefined {
  return application.angularBasePath
    ? `${application.angularBasePath}/app${path}`
    : undefined;
}

/**
 * Resolve where a module is entered: the destination of the app switcher and of
 * the default post-authentication redirect.
 *
 * Modules whose `defaultEntryShell` is Angular are entered through their
 * AngularJS shell, every other module through its React shell. A module set to
 * Angular entry without an Angular shell falls back to React so the switcher can
 * never point at a path nothing serves.
 *
 * @param application - Application being entered.
 * @returns Entry path, with a trailing slash (e.g. "/reporting/app/").
 */
export function getApplicationEntryPath(application: InvApplication): string {
  const angularEntryPath =
    application.defaultEntryShell === EntryShell.Angular
      ? getAngularModuleUrl(application, '/')
      : undefined;

  return angularEntryPath ?? `${application.basePath}/`;
}

/**
 * Build the application's base URL.
 * @param environment - Environment ID (e.g. "qa10").
 * @param applicationBasePath - Application's base path _including_ the leading "/" (e.g. "/customercare").
 * @returns Application URL.
 */
export function getApplicationBaseUrl(
  environment: string,
  applicationBasePath: string
): string {
  return `https://invision.${environment}.ascendon.tv${applicationBasePath}`;
}

// @ts-ignore : implicit any
function hasConfigAccess(attributes, app) {
  return (
    hasBooleanAccess(attributes, app.securityAttributeId) &&
    hasBooleanAccess(attributes, CODE_TABLE_ADMINISTRATION_SEC_ATTR_ID)
  );
}

// @ts-ignore : implicit any
export function makeSelectedApplication(allApps, securityAttributeId) {
  const clonedApplications = cloneDeep(allApps);
  // @ts-ignore : implicit any
  clonedApplications.forEach((application) => {
    application.isActive = false;
  });
  // @ts-ignore : implicit any
  const applicationToSelect = clonedApplications.find((application) => {
    return application.securityAttributeId === securityAttributeId;
  });

  if (applicationToSelect) {
    applicationToSelect.isActive = true;
  }

  return clonedApplications;
}
